import { ActionButton } from "components/actionButton/actionButton";
import { isParameterizedActionDefinition } from "components/actions/actions";
import { InputComponent, isInputComponent, isRestrictableComponent, isValueComponent } from "components/components/Component";
import { Form } from "components/form/form";
import { Renderer } from "components/Renderer";
import { buildConstraints } from "components/restrictions/constraints";
import { DependencyType } from "components/restrictions/dependencies/Dependency";
import { DependencyRunner } from "components/restrictions/dependencies/DependencyRunner";
import { FormRestrictions, isHidden, RestrictionAttribute, TableRestrictions } from "components/restrictions/Restrictions";
import { ColumnDefinition } from "./columns/ColumnDefinitions";
import { ComponentCells } from "./componentCellRenderer";
import { TableRestrictionDataModel } from "./columns/data/tableRestrictionDataModel";
import { Table } from "./table";

export class TableRestrictionsImpl {

    public columnConstraints: {};
    private columnRestrictions: FormRestrictions;
    private filterRestrictions: FormRestrictions;

    public init(restrictions: TableRestrictions, table: Table) {
        if (restrictions.columns != null) {
            this.columnConstraints = buildConstraints(restrictions.columns, table);
            this.columnRestrictions = restrictions.columns;
        }
        this.filterRestrictions = restrictions.filter;
    }

    public applyToComponent(input: InputComponent, cd: ColumnDefinition) {
        if (this.columnRestrictions != null) {
            for (const key in this.columnRestrictions) {
                if (key === cd.property && isRestrictableComponent(input)) {
                    input.applyRestrictions(this.columnRestrictions[key]);
                }
            }
        }
    }

    public columnsToHide(): string[] {
        let columnsToHide: string[] = [];
        if (this.columnRestrictions != null) {
            // tslint:disable-next-line: forin
            for (let columnRestricion in this.columnRestrictions) {
                let restriction = this.columnRestrictions[columnRestricion];
                if (isHidden(restriction)) {
                    columnsToHide.push(columnRestricion);
                }
            }
        }
        return columnsToHide;
    }

    public applyRestriciontsToActions(restrictions: TableRestrictions, actions) {
        if (restrictions.actions != null) {
            for (let actionRestrictionKey in restrictions.actions) {
                if (restrictions.actions.hasOwnProperty(actionRestrictionKey)) {
                    for (let actionButton of actions) {
                        if (actionRestrictionKey === actionButton.action.actionName) {
                            if (isParameterizedActionDefinition(actionButton.action)) {
                                actionButton.action.parametersForm.restrictions =
                                    restrictions.actions[actionRestrictionKey];
                            }
                        }
                    }
                }
            }
        }
    }

    public applyRestriciontsToButtons(restrictions: TableRestrictions, buttons: ActionButton[]) {
        if (buttons == null) {
            return;
        }
        if (restrictions.actionButtons != null) {
            for (let actionRestrictionKey in restrictions.actionButtons) {
                if (restrictions.actionButtons.hasOwnProperty(actionRestrictionKey)) {
                    for (let actionButton of buttons) {
                        if (actionRestrictionKey === actionButton.definition?.action.actionName) {
                            actionButton.applyRestrictions(restrictions.actionButtons[actionRestrictionKey]);
                        }
                    }
                }
            }
        }
    }

    public isColumnReadOnly(property: string) {
        if (this.columnRestrictions == null) {
            return false;
        }
        let columnRestrictions = this.columnRestrictions[property];
        if (columnRestrictions == null || columnRestrictions.attributes == null) {
            return false;
        } else {
            return columnRestrictions.attributes.includes(RestrictionAttribute.readonly);
        }
    }

    public applyDependenciesToFilter(filter: Form, dataModel: TableRestrictionDataModel) {
        if (filter == null) {
            return;
        }
        const data = {
            ...filter.currentData,
            _parent_: dataModel.parentData,
        };

        let dr = new DependencyRunner(data, this.filterRestrictions);
        Renderer.iterateComponentTree(filter, isValueComponent, (c) => {
            dr.applyDependencies(c as InputComponent, filter);
        });
    }

    public applyDependenciesToRow(componentCells: ComponentCells, target: Element, rowModel: any[], dataModel: TableRestrictionDataModel) {
        const components = componentCells.getRowComponents(target);
        let dataHolder = dataModel.rowDataHolder(components, rowModel);
        const data = {
            ...dataHolder.rowDataObject,
            _parent_: dataModel.parentData,
            _filter_: dataModel.filterData,
        };

        const dr = new DependencyRunner(data, this.columnRestrictions);
        for (const component of components) {
            dr.applyDependencies(component, dataHolder);
        }
    }

    public applyDependenciesToNewRow(components: InputComponent[], rowModel: any[], dataModel: TableRestrictionDataModel) {
        let dataHolder = dataModel.rowDataHolder(components, rowModel);
        const data = {
            ...dataHolder.rowDataObject,
            _parent_: dataModel.parentData,
            _filter_: dataModel.filterData,
        };
        let dr;
        if (rowModel == null) {
            // if rowModel is null we must only apply option dependencies
            let optionrestrictions = {};
            for (const key in this.columnRestrictions) {
                optionrestrictions[key] = [];
                optionrestrictions[key].attributes = this.columnRestrictions[key].attributes;
                optionrestrictions[key].dependencies = (this.columnRestrictions[key].dependencies ?? []).filter(this.nullModelDependencies);
            }
            dr = new DependencyRunner(data, optionrestrictions);
        } else {
            dr = new DependencyRunner(data, this.columnRestrictions);
        }
        for (const component of components) {
            dr.applyDependencies(component, dataHolder);
        }
    }

    /**
     * Filter for dependencies that can be applied when the model is null
     * @param d 
     */
    private nullModelDependencies = d => {
        return d.type == DependencyType.queryParameters || d.type == DependencyType.disabled || d.type == DependencyType.enabled || d.type == DependencyType.disabledOptions
    };

    public setReadOnlyColumn(ro: boolean, targetColumn: string) {
        this.columnRestrictions = this.columnRestrictions ?? {};
        this.columnRestrictions[targetColumn] = this.columnRestrictions[targetColumn] ?? { attributes: [] };
        if (ro) {
            if (!this.columnRestrictions[targetColumn].attributes.includes(RestrictionAttribute.readonly)) {
                this.columnRestrictions[targetColumn].attributes.push(RestrictionAttribute.readonly);
            }
        } else {
            this.columnRestrictions[targetColumn].attributes.splice(
                this.columnRestrictions[targetColumn].attributes.indexOf(RestrictionAttribute.readonly),
                1,
            );
        }
    }
}
